// src/codebase/pages/RepoOverviewPage.jsx
//
// Repository detail / analysis page. Route: /codebase/repos/:repoId.
//
// Rebuilt onto real Tailwind SurfaceCard (default variant) with a PageHeader,
// a Tabs strip (shared/ui/Tabs, the same segmented-pill component
// connectors/ConnectorsPage.jsx uses), Alert-based error/failure banners,
// and Tailwind-styled KPI/AI-insight/plan blocks.
//
// The tab split (Trends & Modernization / AI Insights / Diagram / Findings)
// replaces two 2-column card rows that used to sit side by side — when one
// side had far more content than the other, the shorter card stretched to
// match, leaving a large empty gap. Structure borrowed from
// golive/pages/RunDetailPage.jsx (breadcrumb → title → status alert → tabs,
// one section visible at a time — nothing is ever paired side-by-side there
// either), implemented with the shared/ui Tabs component rather than
// GoLive's own bespoke tab bar so this reads as the same design system as
// Connections/Users/Roles, not a second visual language.
//
// MermaidViewer/TrendChart/ProgressBar/FindingsTable keep their own existing
// styling — none of the four is chrome this page owns, and each is a
// single-consumer component not worth re-styling here (see
// .claude/skills/frontend-component/SKILL.md point 3). codebase.css stays
// imported for their sake (.mmd-*, .btn, .muted, .mono, .small, .error).

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { PageHeader, SurfaceCard, ScoreChip, Button, FormField, Select, Input, Alert, Tabs, useDiscardConfirm } from "../../shared/ui";
import Badge from "../components/Badge.jsx";
import Modal from "../components/Modal.jsx";
import FindingsTable from "../components/FindingsTable.jsx";
import MermaidViewer from "../components/MermaidViewer.jsx";
import TrendChart from "../components/TrendChart.jsx";
import ProgressBar from "../components/ProgressBar.jsx";
import RepoFormModal from "../components/RepoFormModal.jsx";

import { repoApi } from "../../api/repoApi";
import { analysisApi } from "../../api/analysisApi";
import { getApiError } from "../../api/client";

import * as XLSX from "xlsx";

import "../styles/codebase.css";
import {
  Play,
  RefreshCw,
  ShieldAlert,
  Wrench,
  Network,
  FileText,
  Download,
  Activity,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

const TABS = [
  { key: "trends", label: "Trends & Modernization", icon: Activity },
  { key: "ai", label: "AI Insights", icon: Sparkles },
  { key: "diagram", label: "Diagram", icon: Network },
  { key: "findings", label: "Findings", icon: ShieldAlert },
];

/* ---------------- small local presentational helpers ---------------- */
// Page-local — this markup only has one consumer (the AI-insights render
// below), so it stays here rather than becoming a shared/ui promotion.

function StatRow({ items }) {
  return (
    <div className="flex items-center gap-6 flex-wrap">
      {items.map((it) => (
        <div key={it.label}>
          <p className="text-xs font-medium text-muted uppercase tracking-wide">{it.label}</p>
          <p className="text-xl font-bold text-ink mt-0.5">{it.value}</p>
        </div>
      ))}
    </div>
  );
}

function InsightSection({ title, right, first = false, children }) {
  return (
    <div className={first ? "" : "border-t border-gray-100 pt-4 mt-4"}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

function InsightGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function InsightItemCard({ title, badge, children }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink">{title}</p>
        {badge}
      </div>
      {children}
    </div>
  );
}

function InsightList({ items, max = 10 }) {
  const arr = toList(items).filter(Boolean);
  if (!arr.length) return null;
  return (
    <ul className="list-disc pl-5 space-y-1 text-sm text-ink">
      {arr.slice(0, max).map((x, i) => (
        <li key={i}>{typeof x === "string" ? x : asText(x)}</li>
      ))}
    </ul>
  );
}

function InsightPre({ children }) {
  return (
    <pre className="text-xs font-mono whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg p-3 text-ink overflow-x-auto">
      {children}
    </pre>
  );
}

// Matches golive/components/Cards/ServiceCard.jsx's evidence-reference chip
// exactly — replaces plain "Evidence: <text>" muted text in the AI insight
// cards below.
function EvidenceChip({ children }) {
  return (
    <p className="mt-1.5">
      <span className="text-xs text-muted mr-1.5">Evidence:</span>
      <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-600 rounded border border-slate-200">
        {children}
      </span>
    </p>
  );
}

/* ---------------- helpers ---------------- */

function computeProgress(scan) {
  if (!scan) return 0;
  const st = String(scan.status || "").toLowerCase();

  if (typeof scan.progress_pct === "number") {
    return Math.max(0, Math.min(100, scan.progress_pct));
  }

  if (st === "queued") return 5;

  if (st === "running") {
    const start = scan.started_at ? new Date(scan.started_at).getTime() : Date.now();
    const elapsed = Math.max(0, Date.now() - start);
    const assumedMs = 6 * 60 * 1000;
    return Math.min(95, 10 + Math.round((elapsed / assumedMs) * 85));
  }

  if (st === "completed") return 100;
  if (st === "failed") return 100;
  return 0;
}

function progressLabel(scan, pct) {
  if (!scan) return "";
  const st = String(scan.status || "").toLowerCase();

  const stage = scan.progress_stage ? String(scan.progress_stage) : "";
  if (stage && st === "running") return stage;

  if (st === "queued") return "Queued… waiting for worker";
  if (st === "running") {
    if (pct < 25) return "Cloning / preparing workspace…";
    if (pct < 45) return "Building dependency index…";
    if (pct < 65) return "Static analysis…";
    if (pct < 85) return "Security scan / Sonar…";
    return "Finalizing results…";
  }
  if (st === "completed") return "Completed";
  if (st === "failed") return scan.error ? `Failed: ${scan.error}` : "Failed";
  return "";
}

function toneForSeverity(sev) {
  const s = String(sev || "").toLowerCase();
  if (s === "critical") return "red";
  if (s === "high") return "red";
  if (s === "medium") return "yellow";
  if (s === "low") return "muted";
  return "muted";
}

function toneForPriority(p) {
  const s = String(p || "").toUpperCase();
  if (s === "P0") return "red";
  if (s === "P1") return "blue";
  if (s === "P2") return "muted";
  return "muted";
}

function toneForRiskLevel(level) {
  const s = String(level || "").toLowerCase();
  if (s === "critical") return "red";
  if (s === "high") return "yellow";
  if (s === "medium") return "blue";
  if (s === "low") return "green";
  return "muted";
}

function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function asText(v) {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function pick(obj, keys, fallback = "") {
  if (!isObj(obj)) return fallback;
  for (const k of keys) {
    const val = obj[k];
    if (val === undefined || val === null) continue;
    if (typeof val === "string" && val.trim() === "") continue;
    return val;
  }
  return fallback;
}

function toList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
}

function renderAiInsights(ai) {
  if (!ai) return <p className="text-sm text-muted">No AI insights yet. Run a new scan.</p>;

  // plain text fallback (when backend JSON parse fails)
  if (typeof ai === "string") return <InsightPre>{ai}</InsightPre>;

  const executive = pick(ai, ["executive_summary", "summary", "overview"], "");
  const riskLevel = pick(ai, ["overall_risk_level"], "");
  const strengths = toList(ai.strengths || []);
  const gaps = toList(ai.gaps || []);
  const risks = toList(ai.top_risks || ai.risks || []);
  const recommendations = toList(ai.recommendations || []);
  const nextActions = toList(ai.next_actions || ai.actions || []);
  const roadmap = ai.roadmap || null;
  const quality = ai.quality_notes || null;

  const hasAnything =
    !!executive ||
    !!riskLevel ||
    strengths.length ||
    gaps.length ||
    risks.length ||
    recommendations.length ||
    nextActions.length ||
    !!roadmap ||
    !!quality;

  if (!hasAnything) return <InsightPre>{asText(ai)}</InsightPre>;

  return (
    <div>
      {/* Executive summary + risk */}
      <InsightSection
        first
        title="Executive Summary"
        right={riskLevel ? <Badge tone={toneForRiskLevel(riskLevel)} dot>{String(riskLevel)}</Badge> : null}
      >
        {!executive ? (
          <p className="text-sm text-muted">No executive summary returned.</p>
        ) : Array.isArray(executive) ? (
          <InsightList items={executive} max={20} />
        ) : typeof executive === "string" ? (
          <p className="text-sm text-ink leading-relaxed">{executive}</p>
        ) : (
          <InsightPre>{asText(executive)}</InsightPre>
        )}
      </InsightSection>

      {/* Strengths */}
      {strengths.length > 0 && (
        <InsightSection title="Strengths">
          <InsightList items={strengths} max={12} />
        </InsightSection>
      )}

      {/* Gaps */}
      {gaps.length > 0 && (
        <InsightSection title="Gaps">
          <InsightGrid>
            {gaps.slice(0, 10).map((g, idx) => {
              if (typeof g === "string") {
                return <InsightItemCard key={idx} title={g} />;
              }

              const priority = pick(g, ["priority"], "P2");
              const title = pick(g, ["title", "name", "gap"], "Gap");
              const description = pick(g, ["description"], "");
              const impact = pick(g, ["impact"], "");
              const evidence = pick(g, ["evidence"], "");
              const actions = toList(g.recommended_actions || []);

              return (
                <InsightItemCard
                  key={idx}
                  title={asText(title)}
                  badge={<Badge tone={toneForPriority(priority)} dot>{String(priority)}</Badge>}
                >
                  {description ? <p className="text-sm text-ink mt-1">{asText(description)}</p> : null}
                  {impact ? <p className="text-xs text-muted mt-1">Impact: {asText(impact)}</p> : null}
                  {evidence ? <EvidenceChip>{asText(evidence)}</EvidenceChip> : null}
                  {actions.length ? (
                    <div className="mt-2">
                      <p className="text-xs text-muted mb-1">Recommended actions:</p>
                      <InsightList items={actions} max={8} />
                    </div>
                  ) : null}
                </InsightItemCard>
              );
            })}
          </InsightGrid>
        </InsightSection>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <InsightSection title="Top Risks">
          <InsightGrid>
            {risks.slice(0, 10).map((r, idx) => {
              if (typeof r === "string") {
                return <InsightItemCard key={idx} title={r} />;
              }

              const severity = pick(r, ["severity", "level"], "medium");
              const title = pick(r, ["title", "risk", "name"], "Risk");
              const impact = pick(r, ["impact"], "");
              const evidence = pick(r, ["evidence"], "");
              const mitigation = toList(r.mitigation || []);

              return (
                <InsightItemCard
                  key={idx}
                  title={asText(title)}
                  badge={<Badge tone={toneForSeverity(severity)} dot>{String(severity)}</Badge>}
                >
                  {impact ? <p className="text-sm text-ink mt-1">{asText(impact)}</p> : null}
                  {evidence ? <EvidenceChip>{asText(evidence)}</EvidenceChip> : null}
                  {mitigation.length ? (
                    <div className="mt-2">
                      <p className="text-xs text-muted mb-1">Mitigation:</p>
                      <InsightList items={mitigation} max={8} />
                    </div>
                  ) : null}
                </InsightItemCard>
              );
            })}
          </InsightGrid>
        </InsightSection>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <InsightSection title="Recommendations">
          <InsightGrid>
            {recommendations.slice(0, 12).map((rec, idx) => {
              if (typeof rec === "string") {
                return <InsightItemCard key={idx} title={rec} />;
              }

              const priority = pick(rec, ["priority"], "P2");
              const title = pick(rec, ["title", "name"], "Recommendation");
              const why = pick(rec, ["why"], "");
              const steps = toList(rec.steps || []);
              const effort = pick(rec, ["effort_days"], "");
              const roi = pick(rec, ["expected_roi"], "");
              const evidence = pick(rec, ["evidence"], "");

              return (
                <InsightItemCard
                  key={idx}
                  title={asText(title)}
                  badge={<Badge tone={toneForPriority(priority)} dot>{String(priority)}</Badge>}
                >
                  {why ? <p className="text-sm text-ink mt-1">{asText(why)}</p> : null}
                  <p className="text-xs text-muted mt-1.5">
                    {effort !== "" ? (
                      <>
                        Effort: <span className="font-mono">{String(effort)}</span> days •{" "}
                      </>
                    ) : null}
                    ROI: {roi ? asText(roi) : "—"}
                  </p>
                  {steps.length ? (
                    <div className="mt-2">
                      <p className="text-xs text-muted mb-1">Steps:</p>
                      <InsightList items={steps} max={10} />
                    </div>
                  ) : null}
                  {evidence ? <EvidenceChip>{asText(evidence)}</EvidenceChip> : null}
                </InsightItemCard>
              );
            })}
          </InsightGrid>
        </InsightSection>
      )}

      {/* Roadmap */}
      {roadmap && isObj(roadmap) && (
        <InsightSection title="Roadmap">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InsightItemCard title="0–2 weeks">
              {(() => {
                const list = <InsightList items={roadmap["0_2_weeks"] || []} max={10} />;
                return roadmap["0_2_weeks"]?.length ? list : <p className="text-sm text-muted mt-1">—</p>;
              })()}
            </InsightItemCard>
            <InsightItemCard title="2–6 weeks">
              {(() => {
                const list = <InsightList items={roadmap["2_6_weeks"] || []} max={10} />;
                return roadmap["2_6_weeks"]?.length ? list : <p className="text-sm text-muted mt-1">—</p>;
              })()}
            </InsightItemCard>
            <InsightItemCard title="6–12 weeks">
              {(() => {
                const list = <InsightList items={roadmap["6_12_weeks"] || []} max={10} />;
                return roadmap["6_12_weeks"]?.length ? list : <p className="text-sm text-muted mt-1">—</p>;
              })()}
            </InsightItemCard>
          </div>
        </InsightSection>
      )}

      {/* Quality Notes */}
      {quality && isObj(quality) && (
        <InsightSection title="Quality Notes">
          <InsightGrid>
            {[
              ["Security", quality.security],
              ["Architecture", quality.architecture],
              ["Testing", quality.testing],
              ["CI/CD", quality.ci_cd],
              ["Observability", quality.observability],
              ["Documentation", quality.documentation],
              ["Performance", quality.performance],
            ].map(([k, v], idx) => (
              <InsightItemCard key={idx} title={k}>
                <p className="text-xs text-muted mt-1">{v ? asText(v) : "—"}</p>
              </InsightItemCard>
            ))}
          </InsightGrid>
        </InsightSection>
      )}

      {/* Next Actions */}
      {nextActions.length > 0 && (
        <InsightSection title="Next Actions">
          <InsightGrid>
            {nextActions.slice(0, 12).map((a, idx) => {
              if (typeof a === "string") {
                return <InsightItemCard key={idx} title={a} />;
              }

              const priority = pick(a, ["priority"], "P2");
              const action = pick(a, ["action", "title", "name"], "Action");
              const owner = pick(a, ["owner"], "Team");
              const effort = pick(a, ["effort_days"], "");
              const evidence = pick(a, ["evidence"], "");

              return (
                <InsightItemCard
                  key={idx}
                  title={asText(action)}
                  badge={<Badge tone={toneForPriority(priority)} dot>{String(priority)}</Badge>}
                >
                  <p className="text-xs text-muted mt-1">
                    Owner: <span className="font-mono">{asText(owner)}</span>
                    {effort !== "" ? (
                      <>
                        {" "}
                        • Effort: <span className="font-mono">{String(effort)}</span> days
                      </>
                    ) : null}
                  </p>
                  {evidence ? <EvidenceChip>{asText(evidence)}</EvidenceChip> : null}
                </InsightItemCard>
              );
            })}
          </InsightGrid>
        </InsightSection>
      )}
    </div>
  );
}

/* Download helper: pulls ALL findings pages */
async function fetchAllFindings(lastScanId) {
  if (!lastScanId) return [];
  const pageSize = 1000;
  let offset = 0;
  let all = [];

  while (true) {
    const res = await analysisApi.getScanResults(lastScanId, { limit: pageSize, offset });
    const items = res.data?.items || [];
    all = all.concat(items);
    const total = res.data?.total || all.length;

    offset += items.length;
    if (items.length === 0 || offset >= total) break;
    if (offset > 20000) break;
  }
  return all;
}

export default function RepoOverviewPage() {
  const { repoId } = useParams();

  const [tab, setTab] = useState("trends");

  const [repo, setRepo] = useState(null);
  const [health, setHealth] = useState(null);
  const [trends, setTrends] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const [modern, setModern] = useState(null);

  const [findings, setFindings] = useState([]);
  const [findingsTotal, setFindingsTotal] = useState(0);

  const [filters, setFilters] = useState({
    category: "",
    severity: "",
    limit: 200,
    offset: 0,
  });

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // scan
  const [scanModal, setScanModal] = useState(false);
  const [scanForm, setScanForm] = useState({
    scan_type: "full",
    run_sonar: true,
    run_ai: true,
  });
  const [scanDirty, setScanDirty] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);

  function closeScanModal() {
    setScanModal(false);
    setScanDirty(false);
  }

  const { requestClose: requestScanClose, confirmDialog: scanConfirmDialog } = useDiscardConfirm(
    scanDirty,
    closeScanModal
  );

  // edit
  const [editOpen, setEditOpen] = useState(false);

  const lastScanId = useMemo(
    () => health?.last_scan_id || repo?.last_scan_id || null,
    [health, repo]
  );

  const pct = computeProgress(scanStatus);
  const pctLabel = progressLabel(scanStatus, pct);

  const loadFindings = async (sid, f) => {
    const res = await analysisApi.getScanResults(sid, {
      category: f.category || undefined,
      severity: f.severity || undefined,
      limit: f.limit || 200,
      offset: f.offset || 0,
    });
    setFindings(res.data.items || []);
    setFindingsTotal(res.data.total || 0);
  };

  const loadAll = async () => {
    setErr("");
    setBusy(true);
    try {
      const [r, h, t, d, m] = await Promise.all([
        repoApi.get(repoId),
        analysisApi.getRepoHealth(repoId),
        analysisApi.getRepoTrends(repoId, 25),
        analysisApi.getDiagram(repoId),
        analysisApi.getModernization(repoId),
      ]);

      setRepo(r.data);
      setHealth(h.data);
      setTrends(t.data);
      setDiagram(d.data);
      setModern(m.data);

      const sid = h.data?.last_scan_id;
      if (sid) {
        await loadFindings(sid, filters);
      } else {
        setFindings([]);
        setFindingsTotal(0);
      }
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  // Poll scan status when scanId is set
  useEffect(() => {
    if (!scanId) return;

    let timer = null;
    let stopped = false;

    const tick = async () => {
      try {
        const res = await analysisApi.getScan(scanId);
        if (stopped) return;
        setScanStatus(res.data);

        const st = String(res.data?.status || "").toLowerCase();
        if (st === "completed" || st === "failed") {
          if (timer) clearInterval(timer);
          timer = null;
          await loadAll();
        }
      } catch {
        // ignore polling errors
      }
    };

    tick();
    timer = setInterval(tick, 2000);

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId]);

  const startScan = async () => {
    setErr("");
    try {
      const res = await analysisApi.startScan({
        repo_id: Number(repoId),
        scan_type: scanForm.scan_type,
        run_sonar: !!scanForm.run_sonar,
        run_ai: !!scanForm.run_ai,
        options_json: {},
      });

      setScanId(res.data.id);
      setScanStatus(res.data);
      closeScanModal();
    } catch (e) {
      setErr(getApiError(e));
    }
  };

  const applyFilters = async () => {
    if (!lastScanId) return;
    await loadFindings(lastScanId, filters);
  };

  const downloadExcel = async () => {
    setErr("");
    try {
      const wb = XLSX.utils.book_new();

      const repoSheet = XLSX.utils.json_to_sheet([
        {
          id: repo?.id,
          name: repo?.name,
          provider: repo?.provider,
          repo_url: repo?.repo_url,
          default_branch: repo?.default_branch,
          last_scan_id: lastScanId || "",
          last_scan_at: health?.last_scan_at || "",
          health_score: health?.health_score ?? 0,
        },
      ]);
      XLSX.utils.book_append_sheet(wb, repoSheet, "Repo");

      const counts = health?.summary?.counts || {};
      const metrics = health?.summary?.static_metrics || {};
      const sec = health?.summary?.security_metrics || {};
      const sonar = health?.summary?.sonar_metrics || {};

      const summarySheet = XLSX.utils.json_to_sheet([
        {
          findings_total: counts.findings_total ?? 0,
          critical: counts.critical ?? 0,
          high: counts.high ?? 0,
          medium: counts.medium ?? 0,
          low: counts.low ?? 0,
          loc: metrics.loc ?? 0,
          files: metrics.files ?? 0,
          complexity_score: metrics.complexity_score ?? 0,
          security_findings: sec.security_findings ?? 0,
          security_critical: sec.critical ?? 0,
          security_high: sec.high ?? 0,
          sonar_bugs: sonar.bugs ?? "",
          sonar_vulnerabilities: sonar.vulnerabilities ?? "",
          sonar_code_smells: sonar.code_smells ?? "",
        },
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

      // AI sheet (high-level fields)
      const ai = health?.summary?.ai_insights;
      const aiErr = health?.summary?.ai_insights_error;
      const aiSheet = XLSX.utils.json_to_sheet([
        {
          ai_available: !!ai && !aiErr,
          ai_error: aiErr || "",
          overall_risk_level: typeof ai === "object" ? ai?.overall_risk_level || "" : "",
          executive_summary:
            typeof ai === "string"
              ? ai
              : ai?.executive_summary || ai?.summary || ai?.overview || "",
          ai_json: typeof ai === "string" ? "" : ai ? JSON.stringify(ai) : "",
        },
      ]);
      XLSX.utils.book_append_sheet(wb, aiSheet, "AI_Insights");

      const modernItems = (modern?.items || []).map((it) => ({
        priority: it.priority,
        title: it.title,
        effort_days: it.effort_days,
        expected_roi: it.expected_roi,
        details: it.details,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(modernItems.length ? modernItems : [{ note: "No modernization items." }]),
        "Modernization"
      );

      const trendRows = (trends?.points || []).map((p) => ({
        scan_id: p.scan_id,
        at: p.at,
        findings_total: p.findings_total,
        critical: p.critical,
        high: p.high,
        complexity: p.complexity,
        loc: p.loc,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(trendRows.length ? trendRows : [{ note: "No trend points." }]),
        "Trends"
      );

      const allFindings = await fetchAllFindings(lastScanId);
      const findingsRows = allFindings.map((f) => ({
        id: f.id,
        category: f.category,
        severity: f.severity,
        rule_id: f.rule_id,
        file_path: f.file_path,
        line: f.line,
        message: f.message,
        created_at: f.created_at,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(findingsRows.length ? findingsRows : [{ note: "No findings." }]),
        "Findings"
      );

      const filenameSafe = (repo?.name || "repo").replace(/[^\w-]+/g, "_");
      XLSX.writeFile(wb, `${filenameSafe}_codebase_report.xlsx`);
    } catch (e) {
      setErr(getApiError(e));
    }
  };

  const score = health?.health_score ?? 0;
  const ai = health?.summary?.ai_insights;
  const aiErr = health?.summary?.ai_insights_error;

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: "Repositories", href: "/codebase" },
          { label: repo?.name || "Repository" },
        ]}
        title={repo?.name || "Repository"}
        subtitle={
          repo && (
            <span className="font-mono text-xs">
              {repo.repo_url}
              <span className="mx-2 text-muted font-sans">•</span>
              <span className="font-sans">Provider:</span> {repo.provider}
              <span className="mx-2 text-muted font-sans">•</span>
              <span className="font-sans">Branch:</span> {repo.default_branch || "main"}
            </span>
          )
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="secondary" onClick={loadAll} loading={busy}>
              <RefreshCw size={16} /> Refresh
            </Button>
            <Button variant="secondary" onClick={downloadExcel} disabled={!repo}>
              <Download size={16} /> Download Excel
            </Button>
            <Button variant="primary" onClick={() => setScanModal(true)}>
              <Play size={16} /> Run Scan
            </Button>
          </div>
        }
      />

      {err && (
        <Alert variant="danger" title="Something went wrong" className="mb-5">
          {err}
        </Alert>
      )}

      {scanStatus && (
        <div className="mb-5">
          <ProgressBar
            title={`Scan #${scanStatus.id} • ${scanStatus.status}`}
            percent={pct}
            subtitle={pctLabel}
          />
        </div>
      )}

      {scanStatus?.status === "failed" && scanStatus?.error && (
        <Alert variant="danger" title="Scan failed" className="mb-5">
          <span className="font-mono">{scanStatus.error}</span>
        </Alert>
      )}

      {/* ---------- KPIs ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <SurfaceCard
          title="Repo Health"
          subtitle={
            health?.last_scan_at
              ? `Last scan: ${new Date(health.last_scan_at).toLocaleString()}`
              : "No scans yet"
          }
          right={<ScoreChip score={score} />}
        >
          <StatRow
            items={[
              { label: "Critical", value: health?.summary?.counts?.critical ?? 0 },
              { label: "High", value: health?.summary?.counts?.high ?? 0 },
              { label: "Total", value: health?.summary?.counts?.findings_total ?? 0 },
            ]}
          />
        </SurfaceCard>

        <SurfaceCard title="Static Metrics" subtitle="From last scan (heuristic)">
          <StatRow
            items={[
              { label: "LOC", value: health?.summary?.static_metrics?.loc ?? 0 },
              { label: "Files", value: health?.summary?.static_metrics?.files ?? 0 },
              { label: "Complexity", value: health?.summary?.static_metrics?.complexity_score ?? 0 },
            ]}
          />
        </SurfaceCard>

        <SurfaceCard title="Security" subtitle="Secrets + unsafe patterns">
          <StatRow
            items={[
              { label: "Findings", value: health?.summary?.security_metrics?.security_findings ?? 0 },
              { label: "Critical", value: health?.summary?.security_metrics?.critical ?? 0 },
              { label: "High", value: health?.summary?.security_metrics?.high ?? 0 },
            ]}
          />
          <p className="text-xs text-muted mt-3">
            Tip: Filter <span className="font-medium text-ink">Category = security</span> to view secrets &
            unsafe patterns in Findings.
          </p>
        </SurfaceCard>
      </div>

      {/* ---------- Tabs ---------- */}
      {/* One section visible at a time — nothing is ever paired side-by-side
          in a grid anymore, so a short section can no longer get stretched
          to match a long neighbor's height (see the file header comment). */}
      <Tabs tabs={TABS} active={tab} onChange={setTab} className="mt-5 mb-4" />

      {tab === "trends" && (
        <div className="space-y-4">
          <SurfaceCard title="Trends" subtitle="Findings + complexity across scans" right={<Activity size={18} className="text-muted" />}>
            <TrendChart points={trends?.points || []} />
          </SurfaceCard>

          <SurfaceCard
            title="Modernization Plan"
            subtitle={modern?.generated_by ? `Generated by: ${modern.generated_by}` : "Plan"}
            right={<Wrench size={18} className="text-muted" />}
          >
            {!modern ? (
              <p className="text-sm text-muted">Loading…</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted">{modern.summary || "—"}</p>
                {(modern.items || []).length === 0 ? (
                  <p className="text-sm text-muted">No modernization items yet.</p>
                ) : (
                  modern.items.map((it, idx) => (
                    <div key={idx} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-ink">{it.title}</p>
                        <Badge tone={it.priority === "P0" ? "red" : it.priority === "P1" ? "blue" : "muted"} dot>
                          {it.priority}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted mt-1">
                        Effort: <span className="font-mono">{it.effort_days}</span> days • ROI:{" "}
                        {it.expected_roi || "—"}
                      </p>
                      {it.details && <p className="text-sm text-ink mt-1">{it.details}</p>}
                    </div>
                  ))
                )}
              </div>
            )}
          </SurfaceCard>
        </div>
      )}

      {tab === "ai" && (
        <SurfaceCard
          title="AI Insights (Llama)"
          subtitle="Comprehensive AI report from scan summary + findings sample"
          right={<Sparkles size={18} className="text-muted" />}
        >
          {aiErr ? (
            <Alert variant="warning" icon={AlertTriangle} title="AI Insights not available">
              <span className="font-mono text-xs block mt-1">{String(aiErr)}</span>
              <span className="block mt-1.5">Run a new scan after enabling AI in backend.</span>
            </Alert>
          ) : (
            renderAiInsights(ai)
          )}
        </SurfaceCard>
      )}

      {tab === "diagram" && (
        <SurfaceCard title="Architecture Diagram" subtitle="Auto-generated Mermaid" right={<Network size={18} className="text-muted" />}>
          <MermaidViewer code={diagram?.mermaid} />
        </SurfaceCard>
      )}

      {tab === "findings" && (
        <SurfaceCard
          title="Findings"
          subtitle={`From last scan • Total: ${findingsTotal}`}
          right={<ShieldAlert size={18} className="text-muted" />}
        >
          {!lastScanId ? (
            <p className="text-sm text-muted">
              No scan results yet. Click <span className="font-medium text-ink">Run Scan</span> to generate
              findings.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                <FormField label="Category">
                  <Select
                    options={[
                      { value: "", label: "All" },
                      { value: "security", label: "security" },
                      { value: "sonar", label: "sonar" },
                      { value: "smell", label: "smell" },
                      { value: "duplication", label: "duplication" },
                      { value: "complexity", label: "complexity" },
                    ]}
                    value={filters.category}
                    onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                  />
                </FormField>

                <FormField label="Severity">
                  <Select
                    options={[
                      { value: "", label: "All" },
                      { value: "critical", label: "critical" },
                      { value: "high", label: "high" },
                      { value: "medium", label: "medium" },
                      { value: "low", label: "low" },
                      { value: "info", label: "info" },
                    ]}
                    value={filters.severity}
                    onChange={(e) => setFilters((p) => ({ ...p, severity: e.target.value }))}
                  />
                </FormField>

                <FormField label="Limit">
                  <Input
                    type="number"
                    value={filters.limit}
                    onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value) || 200 }))}
                  />
                </FormField>

                <Button variant="secondary" onClick={applyFilters}>
                  <FileText size={16} /> Apply
                </Button>
              </div>

              <FindingsTable items={findings} />
            </div>
          )}
        </SurfaceCard>
      )}

      {/* ---------- Scan Modal ---------- */}
      <Modal
        open={scanModal}
        title="Run Scan"
        onClose={requestScanClose}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeScanModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={startScan}>
              Start Scan
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Scan Type">
              <Select
                options={[
                  { value: "full", label: "full" },
                  { value: "incremental", label: "incremental" },
                  { value: "sonar_only", label: "sonar_only" },
                ]}
                value={scanForm.scan_type}
                onChange={(e) => {
                  setScanForm((p) => ({ ...p, scan_type: e.target.value }));
                  setScanDirty(true);
                }}
              />
            </FormField>

            <FormField label="Run Sonar">
              <Select
                options={[
                  { value: "yes", label: "yes" },
                  { value: "no", label: "no" },
                ]}
                value={scanForm.run_sonar ? "yes" : "no"}
                onChange={(e) => {
                  setScanForm((p) => ({ ...p, run_sonar: e.target.value === "yes" }));
                  setScanDirty(true);
                }}
              />
            </FormField>
          </div>

          <FormField
            label="AI Insights (Llama)"
            helperText="Generates executive summary + risks + gaps + recommendations + next actions."
          >
            <Select
              options={[
                { value: "yes", label: "yes" },
                { value: "no", label: "no" },
              ]}
              value={scanForm.run_ai ? "yes" : "no"}
              onChange={(e) => {
                setScanForm((p) => ({ ...p, run_ai: e.target.value === "yes" }));
                setScanDirty(true);
              }}
            />
          </FormField>
        </div>
      </Modal>
      {scanConfirmDialog}

      <RepoFormModal
        key={`${repo?.id}-${repo?.updated_at}`}
        open={editOpen}
        repo={repo}
        onClose={() => setEditOpen(false)}
        onSubmit={async (payload) => {
          const res = await repoApi.update(repoId, payload);
          await loadAll();
          return res.data;
        }}
      />
    </div>
  );
}
